import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import * as EventEmitter from 'events';
import * as moment from 'moment';
import { FormService } from '../../form.service';
import { BaseControlValueAccessorComponent } from '../base-control-value-accessor/base-control-value-accessor.component';
import { TranslateService } from '@ngx-translate/core';
import { BaseService } from 'src/app/core/components/base/base.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-custom-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true,
    },
  ],
})
export class CustomDatePickerComponent extends BaseControlValueAccessorComponent implements OnInit, OnDestroy {
  calendarShown = false;
  placeHolder = this.translate.instant('placeholders.selectDate');
  locale = 'en';
  @Input() title = this.translate.instant('placeholders.selectDate');
  @Input() withTime = true;
  @Input() beginDateWithTime = false;
  @Input() endDateWithTime = false;
  @Input() required = false;
  @Input() usedInTable = false;
  @Input() dateRangePicker = false;
  @Input() formControl = new UntypedFormControl();
  @Output() emitSelectedDate = new EventEmitter();
  dateRangeFrom: string | any = '';
  dateRangeTo: string | any = '';
  dayValue: number | null = null;
  navDate: any;
  weekDaysHeaderArr: Array<string> = [];
  gridArr: Array<any> = [];
  emptyCellGap = 6;
  selectedDate: any;
  timeFormPicked = '';
  timeToPicked = '';
  dateMonthIsSame = false;
  @ViewChild('timePickerInput', { static: true }) timePicker: ElementRef;
  currentLanguageSub: Subscription

  constructor(public formService: FormService, private readonly translate: TranslateService, private readonly baseService: BaseService) {
    super();
  }

  ngOnInit(): void {
    this.currentLanguageSub = this.baseService.currentLanguage$.subscribe((lang) => {
      this.locale = lang.locale;
      moment.locale(this.locale);
    });

    moment.locale(this.locale);

    if (this.dateRangePicker) {
      this.placeHolder = this.translate.instant('placeholders.selectDateRange');
    }
    this.navDate = moment();
    this.dateMonthIsSame =
      moment(this.navDate).month() === moment(new Date()).month() && moment(this.navDate).year() === moment(new Date()).year();
    this.makeHeader();
    this.makeGrid();
    this.setValidatorToFormControls();
  }

  changeNavMonth(num: number): void {
    this.navDate.add(num, 'month');
    this.makeGrid();
    const currentDate = moment(new Date());
    const selectedDate = moment(this.navDate);
    this.dateMonthIsSame = selectedDate.month() === currentDate.month() && selectedDate.year() === currentDate.year();
    this.dayValue = null;
  }

  changeNavYear(num: number): void {
    this.navDate.add(num, 'year');
    this.makeGrid();
    const currentDate = moment(new Date());
    const selectedDate = moment(this.navDate);
    this.dateMonthIsSame = selectedDate.month() === currentDate.month() && selectedDate.year() === currentDate.year();
    this.dayValue = null;
  }

  makeHeader(): void {
    moment.locale(this.locale);
    const weekDaysArr: Array<number> = this.locale === 'hu' ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 0];
    weekDaysArr.forEach((day) => this.weekDaysHeaderArr.push(moment().weekday(day).format('ddd')));
  }

  makeGrid(): void {
    this.gridArr = [];
    const firstDayDate = moment(this.navDate).startOf('month');
    const weekDateValue = firstDayDate.isoWeekday();
    const initialEmptyCells = weekDateValue === 1 ? 0 : weekDateValue - 1;

    const lastDayDate = moment(this.navDate).endOf('month');
    const lastEmptyCells = 7 - lastDayDate.isoWeekday();

    const daysInMonth = this.navDate.daysInMonth();
    const arrayLength = initialEmptyCells + daysInMonth + lastEmptyCells;

    for (let i = 0; i < arrayLength; i++) {
      const obj: any = {};
      if (i < initialEmptyCells) {
        // Handle the days before the start of the month
        const daysFromPreviousMonth = moment(this.navDate).subtract(1, 'month').endOf('month').date();
        obj.value = daysFromPreviousMonth - (initialEmptyCells - i - 1);
        obj.available = false;
      } else if (i >= initialEmptyCells + daysInMonth) {
        // Handle the days after the end of the month
        obj.value = 1 + (i - initialEmptyCells - daysInMonth);
        obj.available = false;
      } else {
        obj.value = i - initialEmptyCells + 1;
        obj.available = true;
      }
      this.gridArr.push(obj);
    }
  }

  deleteSelectedDate(): void {
    if (this.formControl.disabled) {
      return;
    }

    this.formControl.setValue(null);
  }

  dateFromNum(num: number, referenceDate: any): any {
    const returnDate = moment(referenceDate);
    return returnDate.date(num);
  }

  selectDay(event: Event, day: any): void {
    event.preventDefault();
    if (this.dateRangePicker) {
      const datePicked = this.dateFromNum(day.value, this.navDate);
      if (this.dateRangeFrom && this.dateRangeTo) {
        this.dateRangeFrom = '';
        this.dateRangeTo = '';
      }
      if (this.dateRangeFrom && this.dateRangeFrom?.isSame(datePicked, 'day')) {
        this.dateRangeFrom = '';
        this.formControl.setValue(null);
        return;
      }
      if (this.dateRangeTo && this.dateRangeTo?.isSame(datePicked, 'day')) {
        this.dateRangeTo = '';
      }
      if (!this.dateRangeFrom) {
        this.dateRangeFrom = this.dateFromNum(day.value, this.navDate);
        if (this.beginDateWithTime) {
          this.timePicker.nativeElement.click();
          this.calendarShown = false;
        }
        if (this.dateRangeTo) {
          return;
        }
      }
      if (this.dateRangeFrom && !this.dateRangeTo && datePicked.isAfter(this.dateRangeFrom, 'day')) {
        this.dateRangeTo = this.dateFromNum(day.value, this.navDate);
        if (this.endDateWithTime) {
          this.timePicker.nativeElement.click();
        } else {
          this.formControl.setValue(`${moment(this.dateRangeFrom).format('Y-MM-DD')}, ${moment(this.dateRangeTo).format('Y-MM-DD')}`);
        }
        this.calendarShown = false;
      }
      return;
    }
    if (day.available) {
      if (this.withTime) {
        this.timePicker.nativeElement.click();
      }
      this.selectedDate = this.dateFromNum(day.value, this.navDate);
      this.formControl.setValue(moment(this.selectedDate).format('Y-MM-DD'));
      this.calendarShown = false;
    }
  }

  isSelectedDay(day: any): boolean {
    if (this.dateRangePicker) {
      return (
        new Date(this.dateRangeFrom).toString() === new Date(this.dateFromNum(day.value, this.navDate)).toString() ||
        new Date(this.dateRangeTo).toString() === new Date(this.dateFromNum(day.value, this.navDate)).toString()
      );
    }
    return new Date(this.selectedDate).toString() === new Date(this.dateFromNum(day.value, this.navDate)).toString();
  }

  timePickerEvent(e: string): void {
    if (this.beginDateWithTime && !this.timeFormPicked) {
      this.timeFormPicked = e;
      this.formControl.setValue(`${moment(this.dateRangeFrom).format('Y-M-DD')} ${e}`);
      this.calendarShown = true;
      return;
    }
    if (this.endDateWithTime && !this.timeToPicked) {
      this.timeToPicked = e;
      this.formControl.setValue(
        `${moment(this.dateRangeFrom).format('Y-MM-DD')} ${this.timeFormPicked}, ${moment(this.dateRangeTo).format('Y-MM-DD')} ${e}`
      );
      if (this.dateRangeTo && this.dateRangeFrom) {
        this.dateRangeFrom = '';
        this.dateRangeTo = '';
        this.timeToPicked = '';
        this.timeFormPicked = '';
      }
      return;
    }

    const value = this.formControl.value;
    this.formControl.setValue(`${value} ${e}`);
  }

  timePickCancelled(): void {
    const value = this.formControl.value;
    if (value?.length < 12 || !value) {
      this.dateRangeFrom = '';
      this.selectedDate = null;
      this.formControl.setValue('');
      return;
    }
    if (this.dateRangeTo && this.dateRangeFrom && !this.timeToPicked) {
      this.formControl.setValue(null);
      this.dateRangeFrom = '';
      this.dateRangeTo = '';
      this.timeToPicked = '';
      this.timeFormPicked = '';
    }
  }

  showCalendar(event: Event): void {
    event.preventDefault();
    this.calendarShown = !this.calendarShown;
  }

  private setValidatorToFormControls(): void {
    if (this.required) {
      this.placeHolder = this.placeHolder + ' *';
    }
    if (this.formControl.value) {
      this.formControl.markAsDirty();
      return;
    }
    this.formControl.setValue('');
  }

  ngOnDestroy(): void {
    if (this.currentLanguageSub && !this.currentLanguageSub.closed) {
      this.currentLanguageSub.unsubscribe();
    }
  }
}
